import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Toast } from './Toast';

export interface ToastData {
    id: string;
    title?: string;
    description?: string;
    type?: 'success' | 'error' | 'info' | 'default' | 'undo' | 'warning';
    actionLabel?: string;
    onAction?: () => void;
    duration?: number;
}

interface ToastContextType {
    showToast: (data: Partial<Omit<ToastData, 'id'>> & { message?: string }) => void;
    showUndoToast: (message: string, onUndo: () => void) => void;
    /** Show an error toast */
    showError: (message: string, options?: { title?: string; duration?: number }) => void;
    /** Show a warning toast */
    showWarning: (message: string, options?: { title?: string; duration?: number }) => void;
    /** Show a success toast */
    showSuccess: (message: string, options?: { title?: string; duration?: number }) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

interface ToastProviderProps {
    children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastData[]>([]);

    const showToast = useCallback((data: Partial<Omit<ToastData, 'id'>> & { message?: string }) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        // Backwards compatibility: if message is passed but no title/description
        const title = data.title || (data.description ? undefined : data.message);
        const description = data.description || (data.title ? data.message : undefined);

        setToasts(prev => [...prev, {
            id,
            ...data,
            title,
            description
        }]);
    }, []);

    const showUndoToast = useCallback((message: string, onUndo: () => void) => {
        showToast({
            title: message,
            type: 'undo',
            actionLabel: 'Undo',
            onAction: onUndo,
            duration: 5000,
        });
    }, [showToast]);

    const showError = useCallback((message: string, options?: { title?: string; duration?: number }) => {
        showToast({
            title: options?.title ?? 'Error',
            description: message,
            type: 'error',
            duration: options?.duration ?? 5000,
        });
    }, [showToast]);

    const showWarning = useCallback((message: string, options?: { title?: string; duration?: number }) => {
        showToast({
            title: options?.title ?? 'Warning',
            description: message,
            type: 'warning',
            duration: options?.duration ?? 4000,
        });
    }, [showToast]);

    const showSuccess = useCallback((message: string, options?: { title?: string; duration?: number }) => {
        showToast({
            title: options?.title ?? 'Success',
            description: message,
            type: 'success',
            duration: options?.duration ?? 3000,
        });
    }, [showToast]);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast, showUndoToast, showError, showWarning, showSuccess }}>
            {children}
            {/* Toast Container - Bottom Right */}
            <div className="fixed bottom-6 right-6 z-[1000] flex flex-col gap-2 pointer-events-none">
                {toasts.map(toast => (
                    <div key={toast.id} className="pointer-events-auto">
                        <Toast
                            {...toast}
                            onClose={() => removeToast(toast.id)}
                        />
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};
